/**
 * useMediaQuery — thin wrapper around window.matchMedia.
 *
 * Returns true while the given CSS media query matches, false otherwise.
 * Listens for changes and re-renders automatically.
 *
 * Usage:
 *   const isMobile = useMediaQuery('(max-width: 768px)');
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const getMatches = () =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false;

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Modern browsers support addEventListener on MediaQueryList
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
